import React, { useState, useEffect, useRef } from 'react';

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const DEFAULT_CENTER = { lat: 32.0853, lng: 34.7818 }; // Tel Aviv

/**
 * Shared component: address autocomplete + Google Map with draggable pin.
 *
 * Extracted from AccountModal.js (Task 2, 2026-04-27) so the same
 * autocomplete-and-map UI can be reused in the inline-edit address rendering
 * path in App.js (RecordPage, line ~14808). Owner request: edit-account
 * should have the same Google Maps + address search experience as create.
 *
 * Props:
 * - initialLat / initialLng: number | string — initial map center/marker.
 *     Treated as "initial only" (no live re-positioning if parent state
 *     changes — keeps the component self-contained and avoids a re-init storm
 *     on every keystroke). Parents that need to force a re-init can change
 *     the `key` prop.
 * - onAddressSelected: ({ street, city, state, postalCode, country, lat, lng }) => void
 *     Fires when user picks an address via search OR drags the pin. Country
 *     defaults to "ישראל" if Google didn't return one. Parents should batch
 *     all 7 fields into a single state update to avoid intermediate renders.
 * - height: number (default 250) — map height in pixels.
 *
 * Re-uses CSS classes `address-search-full`, `address-map-full`,
 * `coordinates-display` from afikim-ui.css (defined for the original
 * AccountModal usage).
 */
const AddressAutocompleteWithMap = ({ initialLat, initialLng, onAddressSelected, height = 250 }) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [addressSearch, setAddressSearch] = useState('');
  const [selectedAddress, setSelectedAddress] = useState('');
  const [currentLat, setCurrentLat] = useState(() => {
    const v = parseFloat(initialLat);
    return isNaN(v) ? null : v;
  });
  const [currentLng, setCurrentLng] = useState(() => {
    const v = parseFloat(initialLng);
    return isNaN(v) ? null : v;
  });
  // Live predictions for the search input (Google Places AutocompleteService).
  // Owner-spec 2026-05-25: each keystroke should refresh suggestions.
  const [predictions, setPredictions] = useState([]);
  const [showPredictions, setShowPredictions] = useState(false);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const searchContainerRef = useRef(null);
  // Stable callback ref so the dragend listener doesn't re-bind on every render
  const onAddressSelectedRef = useRef(onAddressSelected);
  useEffect(() => { onAddressSelectedRef.current = onAddressSelected; }, [onAddressSelected]);

  // Init Places AutocompleteService + PlacesService once the script loads.
  // The session-token groups suggest-then-detail calls into one billing unit
  // (Google charges per session, not per keystroke, when one is supplied).
  useEffect(() => {
    if (!mapLoaded || !window.google?.maps?.places) return;
    if (!autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    }
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    // PlacesService needs a real DOM node (or Map). Use a hidden div so we
    // don't depend on the map being mounted (the map is conditional on
    // mapLoaded too but is rendered later in the JSX).
    if (!placesServiceRef.current) {
      const host = document.createElement('div');
      placesServiceRef.current = new window.google.maps.places.PlacesService(host);
    }
  }, [mapLoaded]);

  // Debounced live-prediction fetch.
  useEffect(() => {
    const term = addressSearch.trim();
    if (!autocompleteServiceRef.current || term.length < 2) {
      setPredictions([]);
      return undefined;
    }
    // A "lat, lng" coordinate string won't match Places Autocomplete — clear
    // predictions so no unrelated suggestion shows; Enter/button reverse-geocodes.
    if (/^\s*-?\d{1,3}(?:\.\d+)?\s*[, ]\s*-?\d{1,3}(?:\.\d+)?\s*$/.test(term)) {
      setPredictions([]);
      return undefined;
    }
    const id = setTimeout(() => {
      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: term,
          language: 'he',
          // Israeli addresses only — owner-confirmed this CRM is Israel-only.
          componentRestrictions: { country: 'il' },
          sessionToken: sessionTokenRef.current,
        },
        (preds, status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !preds) {
            setPredictions([]);
            return;
          }
          setPredictions(preds.slice(0, 8));
        },
      );
    }, 200);
    return () => clearTimeout(id);
  }, [addressSearch]);

  // Click-outside closes the predictions dropdown.
  useEffect(() => {
    function onDocMouseDown(e) {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setShowPredictions(false);
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  // Apply a chosen place — used both by the prediction-click flow and by
  // the legacy search-button/Enter flow. Centralizes the marker move +
  // parent notification so both paths stay consistent.
  const applyPlace = (result, prediction = null) => {
    const location = result.geometry?.location;
    if (!location) return;
    const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
    const lng = typeof location.lng === 'function' ? location.lng() : location.lng;
    // 2026-10-26: Google's place details / geocoder now answer Israeli street and
    // city names in English even with language=he (the suggestions are still
    // Hebrew). Keep the Hebrew of the suggestion the user picked.
    const { parts, formatted } = hebrewFromPrediction(
      parseAddressComponents(result.address_components || [], result.formatted_address || ''),
      result.formatted_address || '',
      prediction,
    );
    const street = parts.street || formatted;
    if (mapInstanceRef.current && markerRef.current) {
      mapInstanceRef.current.setCenter({ lat, lng });
      markerRef.current.setPosition({ lat, lng });
    }
    setCurrentLat(lat);
    setCurrentLng(lng);
    setAddressSearch('');
    setPredictions([]);
    setShowPredictions(false);
    setSelectedAddress(formatted);
    // Rotate the session token after a successful selection — Google's
    // documented pattern: token = one session of suggest→detail calls.
    sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    onAddressSelectedRef.current?.({
      street,
      city: parts.city,
      state: parts.state,
      postalCode: parts.postalCode,
      country: parts.country || 'ישראל',
      lat,
      lng,
      formattedAddress: formatted,
    });
  };

  // Click on a prediction → fetch the place details (lat/lng + components).
  const selectPrediction = (pred) => {
    if (!placesServiceRef.current) return;
    placesServiceRef.current.getDetails(
      {
        placeId: pred.place_id,
        fields: ['address_components', 'formatted_address', 'geometry'],
        language: 'he',
        sessionToken: sessionTokenRef.current,
      },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) return;
        applyPlace(place, pred);
      },
    );
  };

  // Load Google Maps script (de-duped: if any other component already loaded
  // it, we just wait for window.google.maps to appear).
  useEffect(() => {
    if (window.google?.maps) {
      setMapLoaded(true);
      return;
    }
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      const check = setInterval(() => {
        if (window.google?.maps) { setMapLoaded(true); clearInterval(check); }
      }, 200);
      const stop = setTimeout(() => clearInterval(check), 10000);
      return () => { clearInterval(check); clearTimeout(stop); };
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=places&language=he`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const checkLoaded = setInterval(() => {
        if (window.google?.maps) { setMapLoaded(true); clearInterval(checkLoaded); }
      }, 100);
      setTimeout(() => clearInterval(checkLoaded), 5000);
    };
    document.head.appendChild(script);
  }, []);

  // Initialize Map + Marker once script + DOM ref are ready.
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || mapInstanceRef.current) return;

    const lat = parseFloat(initialLat);
    const lng = parseFloat(initialLng);
    const center = !isNaN(lat) && !isNaN(lng) ? { lat, lng } : DEFAULT_CENTER;

    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: 15,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
    });

    markerRef.current = new window.google.maps.Marker({
      map: mapInstanceRef.current,
      position: center,
      draggable: true,
    });

    // Reverse-geocode on drag end → notify parent with full address payload.
    markerRef.current.addListener('dragend', () => {
      const pos = markerRef.current.getPosition();
      const dragLat = pos.lat();
      const dragLng = pos.lng();

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat: dragLat, lng: dragLng } }, (results, status) => {
        const formatted = (status === 'OK' && results[0]) ? results[0].formatted_address : '';
        const parts = (status === 'OK' && results[0])
          ? parseAddressComponents(results[0].address_components, formatted)
          : { street: '', city: '', state: '', postalCode: '', country: '' };
        setCurrentLat(dragLat);
        setCurrentLng(dragLng);
        setSelectedAddress(formatted);
        onAddressSelectedRef.current?.({
          street: parts.street || formatted,
          city: parts.city,
          state: parts.state,
          postalCode: parts.postalCode,
          country: parts.country || 'ישראל',
          lat: dragLat,
          lng: dragLng,
          formattedAddress: formatted,
        });
      });
    });

    return () => {
      // Listener cleanup; map ref nulled so a remount can re-init
      if (markerRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(markerRef.current);
      }
      mapInstanceRef.current = null;
      markerRef.current = null;
    };
  }, [mapLoaded]);

  // Forward-geocode the user's typed address → recenter map + notify parent.
  // Used by the Enter key / search button as a fallback when the user
  // didn't pick a live prediction. The prediction-click path uses
  // selectPrediction (above) which routes through PlacesService.getDetails.
  const searchAddress = () => {
    const term = addressSearch.trim();
    if (!term || !window.google?.maps) return;
    const geocoder = new window.google.maps.Geocoder();

    // (b) Coordinates: "lat, lng" → reverse-geocode. Places Autocomplete
    // can't match a raw coordinate string, so detect it here and route to
    // geocode({location}). Fall back to a bare pin if reverse-geocode misses.
    const coord = term.match(/^\s*(-?\d{1,3}(?:\.\d+)?)\s*[, ]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/);
    if (coord) {
      const lat = parseFloat(coord[1]);
      const lng = parseFloat(coord[2]);
      if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === 'OK' && results[0]) applyPlace(results[0]);
          else applyPlace({
            geometry: { location: { lat, lng } },
            formatted_address: `${lat}, ${lng}`,
            address_components: [],
          });
        });
        return;
      }
    }

    // (a) Geocode the exact typed text first — the Geocoding API resolves
    // small Israeli localities that Places Autocomplete misses (with
    // region:'il'). Only fall back to the top live prediction if geocoding
    // finds nothing, so a typed locality is never overridden by an unrelated
    // street/POI prediction.
    geocoder.geocode(
      { address: term, region: 'il', componentRestrictions: { country: 'il' } },
      (results, status) => {
        if (status === 'OK' && results[0]) {
          const p = parseAddressComponents(results[0].address_components || [], results[0].formatted_address || '');
          // 2026-10-26: the geocoder answers in English — prefer the Hebrew suggestion when there is one.
          if (predictions[0] && (hasLatin(p.street) || hasLatin(p.city))) { selectPrediction(predictions[0]); return; }
          applyPlace(results[0]);
          return;
        }
        if (predictions[0]) selectPrediction(predictions[0]);
      },
    );
  };

  return (
    <div className="address-autocomplete-with-map">
      <div
        className="address-search-full"
        ref={searchContainerRef}
        style={{ position: 'relative' }}
      >
        <input
          type="text"
          value={addressSearch}
          onChange={(e) => { setAddressSearch(e.target.value); setShowPredictions(true); }}
          onFocus={() => setShowPredictions(true)}
          placeholder="חפש כתובת..."
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              searchAddress();
            } else if (e.key === 'Escape') {
              setShowPredictions(false);
            }
          }}
        />
        <button type="button" onClick={searchAddress} disabled={!mapLoaded}>
          🔍 חפש
        </button>
        {/* Live predictions dropdown — Owner-spec 2026-05-25: results
            should update on every keystroke and let the user pick by
            click instead of having to press Enter / "חפש". */}
        {showPredictions && predictions.length > 0 && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              insetInlineStart: 0,
              insetInlineEnd: 0,
              background: '#fff',
              border: '1px solid #d8dde6',
              borderRadius: '6px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              zIndex: 1000,
              maxHeight: '320px',
              overflowY: 'auto',
              direction: 'rtl',
            }}
          >
            {predictions.map((p) => (
              <div
                key={p.place_id}
                onMouseDown={(e) => { e.preventDefault(); selectPrediction(p); }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f6fa'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                style={{
                  padding: '10px 14px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f3f3f7',
                  fontSize: '14px',
                  lineHeight: 1.4,
                }}
              >
                <div style={{ fontWeight: 500, color: '#181818' }}>
                  {p.structured_formatting?.main_text || p.description}
                </div>
                {p.structured_formatting?.secondary_text && (
                  <div style={{ fontSize: '12px', color: '#706e6b', marginTop: '2px' }}>
                    {p.structured_formatting.secondary_text}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {mapLoaded && (
        <div ref={mapRef} className="address-map-full" style={{ height: `${height}px` }} />
      )}

      {currentLat != null && currentLng != null && (
        <div className="coordinates-display">
          📍 {parseFloat(currentLat).toFixed(6)}, {parseFloat(currentLng).toFixed(6)}
        </div>
      )}

      {selectedAddress && (
        <div
          className="selected-address-display"
          style={{
            marginTop: 6,
            padding: '8px 10px',
            background: '#f3f6fa',
            border: '1px solid #d8dde6',
            borderRadius: 4,
            fontSize: 13,
            color: '#181818',
            direction: 'rtl',
          }}
          title={selectedAddress}
        >
          📌 {selectedAddress}
        </div>
      )}
    </div>
  );
};

function parseAddressComponents(components, formattedAddress) {
  let streetName = '', streetNumber = '', city = '', state = '', postalCode = '', country = '';
  let sublocality = '', admin2 = '';
  components?.forEach((comp) => {
    if (comp.types.includes('route')) streetName = comp.long_name;
    if (comp.types.includes('street_number')) streetNumber = comp.long_name;
    if (comp.types.includes('locality')) city = comp.long_name;
    if (comp.types.includes('sublocality')) sublocality = comp.long_name;
    if (comp.types.includes('administrative_area_level_2')) admin2 = comp.long_name;
    if (comp.types.includes('administrative_area_level_1')) state = comp.long_name;
    if (comp.types.includes('postal_code')) postalCode = comp.long_name;
    if (comp.types.includes('country')) country = comp.long_name;
  });
  // Israeli settlements (קרני שומרון, etc.) often arrive without a `locality`
  // type — fall back to sublocality, admin_area_level_2, or parse from
  // formatted_address (the second-to-last comma-separated piece is usually city).
  if (!city) city = sublocality || admin2 || '';
  if (!city && formattedAddress) {
    const parts = formattedAddress.split(',').map((s) => s.trim()).filter(Boolean);
    if (parts.length >= 3) city = parts[parts.length - 2];
    else if (parts.length === 2) city = parts[0];
  }
  const street = streetNumber && streetName
    ? `${streetName} ${streetNumber}`
    : streetName || streetNumber || '';
  return { street, city, state, postalCode, country };
}

const LATIN = /[A-Za-z]/;
const HEBREW = /[\u0590-\u05FF]/;

export function hasLatin(v) {
  return LATIN.test(String(v || ''));
}

/**
 * The street / city of a Google answer, in Hebrew when the user picked a Hebrew
 * suggestion and Google answered the details in English (2026-10-26).
 * prediction.structured_formatting: main_text "אקסודוס 34", secondary_text "אשקלון, ישראל".
 * Only the English fields are replaced; a Hebrew answer is returned untouched.
 */
export function hebrewFromPrediction(parts, formatted, prediction) {
  const sf = prediction?.structured_formatting;
  const mainText = String(sf?.main_text || '').trim();
  const cityText = String(sf?.secondary_text || '').split(',')[0].trim();
  if (!sf || !(hasLatin(parts.street) || hasLatin(parts.city))) return { parts, formatted };
  const out = { ...parts };
  if (hasLatin(out.street) && HEBREW.test(mainText)) out.street = mainText;
  if (hasLatin(out.city) && HEBREW.test(cityText)) out.city = cityText;
  if (hasLatin(out.country) || !out.country) out.country = 'ישראל';
  const desc = String(prediction.description || '').trim();
  return { parts: out, formatted: HEBREW.test(desc) ? desc : formatted };
}

export { parseAddressComponents };

export default AddressAutocompleteWithMap;
